import { motion } from 'framer-motion';
import { Heart, MapPin, Users, Sparkles, Mail, Phone } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-maroon-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4af37'%3E%3Cpath d='M30 0l30 30-30 30L0 30z'/%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px' }}
          >
            ABOUT <span className="text-gold-400">PLAN MY SHAADI</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-cream-200 max-w-2xl mx-auto"
            style={{ fontFamily: "'Dancing Script', cursive" }}
          >
            Canada's First South Asian Wedding Marketplace
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gold-200">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-maroon-900" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-maroon-900" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '1px' }}>
                  OUR STORY
                </h2>
              </div>
              
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Welcome to <strong className="text-maroon-700">Plan My Shaadi</strong> — Canada's premier destination for South Asian wedding planning. Based in the heart of <strong className="text-maroon-700">Toronto</strong>, we are passionate about helping couples across Canada create their dream weddings while celebrating the rich traditions and vibrant culture of South Asian celebrations.
              </p>
              
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                We understand that planning a South Asian wedding is a unique journey filled with beautiful traditions, elaborate ceremonies, and countless details. From finding the perfect venue that can accommodate your sangeet, mehndi, and wedding ceremonies, to discovering talented mehndi artists, photographers who understand our cultural nuances, and caterers who can create authentic cuisines — we've built a platform that brings all these exceptional vendors together in one place.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Our mission is simple: <em className="text-maroon-600">to make wedding planning easier, more accessible, and more enjoyable for the South Asian community in Canada</em>. Whether you're planning an intimate gathering or a grand celebration spanning multiple days, Plan My Shaadi connects you with verified, trusted vendors who specialize in bringing South Asian weddings to life.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed">
                We're not just a marketplace — we're a community of families, vendors, and dreamers who believe that every love story deserves a beautiful beginning. Let us help you write the first chapter of your happily ever after.
              </p>
            </div>
          </motion.div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-cream-200 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-gold-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-gold-600" />
              </div>
              <h3 className="font-bold text-maroon-900 text-lg mb-2">Trusted Vendors</h3>
              <p className="text-gray-600 text-sm">Carefully curated vendors who understand South Asian weddings</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-cream-200 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-gold-100 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-gold-600" />
              </div>
              <h3 className="font-bold text-maroon-900 text-lg mb-2">Across Canada</h3>
              <p className="text-gray-600 text-sm">Serving couples in all provinces from coast to coast</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-cream-200 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-gold-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-gold-600" />
              </div>
              <h3 className="font-bold text-maroon-900 text-lg mb-2">Cultural Expertise</h3>
              <p className="text-gray-600 text-sm">Vendors who celebrate and understand our traditions</p>
            </motion.div>
          </div>

          {/* Contact */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-r from-maroon-800 to-maroon-900 rounded-3xl p-8 md:p-12 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '2px' }}>
              GET IN TOUCH
            </h2>
            <p className="text-cream-200 mb-6">
              Have questions or need assistance? We'd love to hear from you!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:admin@planmyshaadi.ca" 
                className="flex items-center gap-2 bg-gold-500 text-maroon-900 px-6 py-3 rounded-xl font-semibold hover:bg-gold-400 transition-colors"
              >
                <Mail className="w-5 h-5" />
                admin@planmyshaadi.ca
              </a>
            </div>
            <p className="text-cream-300 text-sm mt-6">
              Based in Toronto, Ontario 🇨🇦
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
